import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface IgQueuePost {
  id: string;
  project_id: string;
  template_id: string | null;
  category: string;
  format: string;
  caption: string | null;
  hashtags: string[] | null;
  image_url: string | null;
  image_urls: string[];
  media_type: string;
  status: string;
  scheduled_at: string | null;
  priority: number;
  is_recycled: boolean;
  recycled_from: string | null;
  generation_metadata: Record<string, unknown> | null;
  error_message: string | null;
  retry_count: number;
  created_at: string;
  updated_at: string;
}

export function useIgQueue(projectSlug: string, statusFilter?: string) {
  const [posts, setPosts] = useState<IgQueuePost[]>([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.functions.invoke("ig-admin-api", {
      body: { action: "list_queue", project: projectSlug, status: statusFilter || null },
    });
    if (!error && data?.posts) setPosts(data.posts);
    setLoading(false);
  }, [projectSlug, statusFilter]);

  useEffect(() => { fetch(); }, [fetch]);

  const updateItem = useCallback(async (queueId: string, updates: { caption?: string; hashtags?: string[]; scheduled_at?: string }) => {
    const { error } = await supabase.functions.invoke("ig-admin-api", {
      body: { action: "update_queue_item", project: projectSlug, queue_id: queueId, ...updates },
    });
    if (!error) await fetch();
    return !error;
  }, [projectSlug, fetch]);

  const updateCaption = useCallback(async (queueId: string, caption: string) => {
    return updateItem(queueId, { caption });
  }, [updateItem]);

  const deleteItem = useCallback(async (queueId: string) => {
    const { error } = await supabase.functions.invoke("ig-admin-api", {
      body: { action: "delete_queue_item", project: projectSlug, queue_id: queueId },
    });
    if (!error) await fetch();
    return !error;
  }, [projectSlug, fetch]);

  const triggerGenerate = useCallback(async (opts?: { template_id?: string; category?: string; visual_format?: string }) => {
    const { data, error } = await supabase.functions.invoke("ig-admin-api", {
      body: { action: "trigger_generate", project: projectSlug, ...opts },
    });
    if (!error) await fetch();
    return data;
  }, [projectSlug, fetch]);

  const triggerPlan = useCallback(async () => {
    const { data, error } = await supabase.functions.invoke("ig-admin-api", {
      body: { action: "trigger_plan", project: projectSlug },
    });
    if (!error) await fetch();
    return { data, error };
  }, [projectSlug, fetch]);

  const triggerPublish = useCallback(async (queueId?: string) => {
    const { data, error } = await supabase.functions.invoke("ig-admin-api", {
      body: { action: "trigger_publish", project: projectSlug, queue_id: queueId },
    });
    if (!error) await fetch();
    return data;
  }, [projectSlug, fetch]);

  const regenerateImage = useCallback(async (post: IgQueuePost) => {
    const { data, error } = await supabase.functions.invoke("ig-admin-api", {
      body: { action: "trigger_generate", project: projectSlug, template_id: post.template_id, category: post.category, visual_format: post.format },
    });
    if (!error) await fetch();
    return { data, error };
  }, [projectSlug, fetch]);

  const readyCount = posts.filter((p) => p.status === "ready" && p.image_url).length;
  const nextPost = posts.find((p) => p.status === "ready" && p.image_url && p.scheduled_at);

  return {
    posts,
    loading,
    refetch: fetch,
    updateItem,
    updateCaption,
    deleteItem,
    triggerGenerate,
    triggerPlan,
    triggerPublish,
    regenerateImage,
    readyCount,
    nextPost,
  };
}
